'use client'

import { type CSSProperties } from 'react'

import { useMeasure } from '@uidotdev/usehooks'

import { PageLayoutHeader } from '~/components/component-library/PageLayout/components/PageLayoutHeader'
import { PageLayoutTitle } from '~/components/component-library/PageLayout/components/PageLayoutTitle'
import { PageLayoutTopBar } from '~/components/component-library/PageLayout/components/PageLayoutTopBar'
import { PageLayoutProps } from '~/components/component-library/PageLayout/types'
import { useBanner } from '~/hooks/useBanner'
import { useIsMobile } from '~/utils/devices'
import { cn } from '~/utils/tailwind-utils'

export type { PageLayoutProps }

export const PageLayout = ({
  pageType = 'standard',
  leftSidebar,
  rightSidebar,
  header,
  title,
  description,
  actionButtons,
  secondaryButtons,
  breadcrumbs,
  stickyContent,
  children,
}: PageLayoutProps) => {
  const isMobile = useIsMobile()
  const { showBanner } = useBanner()
  const [stickyRef, { height: stickyHeight }] = useMeasure<HTMLDivElement>()

  const hasSidebar = !!(leftSidebar || rightSidebar)

  const maxWidthClasses = {
    'max-w-5xl': pageType === 'standard' && hasSidebar,
    'max-w-lg': pageType === 'standard' && !hasSidebar,
  }

  if (pageType === 'table') {
    const hasTopBar = !!(secondaryButtons || breadcrumbs)

    // data-table-scroll scrolls the table on both axes. The title and
    // stickyContent are its direct children and stick to its left edge, so
    // they stay put while a wide table scrolls sideways. The title scrolls
    // away vertically; stickyContent pins at the top, its height driving
    // --tabs-list-height for the table's sticky <thead>.
    return (
      <div
        className={cn('flex w-full flex-col overflow-hidden', {
          'h-screen': !showBanner,
          'h-[calc(100vh-4rem)] md:h-[calc(100vh-2.5rem)]': showBanner,
        })}
      >
        {hasTopBar && (
          <PageLayoutTopBar
            secondaryButtons={secondaryButtons}
            breadcrumbs={breadcrumbs}
          />
        )}
        <div
          data-table-scroll
          // table-fixed: a no-width column truncates instead of growing to its
          // content and widening the table.
          className="w-full flex-1 overflow-x-auto overflow-y-auto overscroll-x-none [&_table]:table-fixed"
          style={
            stickyContent
              ? ({
                  '--tabs-list-height': `${stickyHeight ?? 0}px`,
                } as CSSProperties)
              : undefined
          }
        >
          {(title || header) && (
            <div
              className={cn(
                'sticky left-0 w-full min-w-0 space-y-6 overflow-hidden px-4 md:px-6 lg:px-8',
                stickyContent ? 'pb-2' : 'pb-4 md:pb-6 lg:pb-8',
                hasTopBar ? 'pt-6 lg:pt-8' : 'pt-[84px] lg:pt-[92px]',
              )}
            >
              <PageLayoutHeader header={header} />
              <PageLayoutTitle title={title} actionButtons={actionButtons} />
            </div>
          )}
          {stickyContent && (
            <div
              ref={stickyRef}
              className="sticky left-0 top-0 z-30 bg-surface"
            >
              {stickyContent}
            </div>
          )}
          {/* container-type lets a table's sticky toolbar size to the visible
              width (100cqw). It sits on this child rather than the scroller,
              where Chrome counts a vertical scrollbar in 100cqw. The title and
              stickyContent stay outside it so they can stick across the
              table's full scroll width. */}
          <div className="[container-type:inline-size]">{children}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto grid w-full">
      <PageLayoutTopBar
        secondaryButtons={secondaryButtons}
        breadcrumbs={breadcrumbs}
      />
      <div className="w-full space-y-8 overflow-x-hidden p-4 py-6 md:p-6 lg:p-8">
        {(title || header) && (
          <div
            className={cn([
              'mx-auto flex w-full flex-col justify-end space-y-6',
              maxWidthClasses,
            ])}
          >
            <PageLayoutHeader header={header} />
            <PageLayoutTitle title={title} actionButtons={actionButtons} />
            {description && <p>{description}</p>}
          </div>
        )}
        <div
          className={cn([
            'mx-auto flex w-full items-start justify-center gap-8',
            maxWidthClasses,
          ])}
        >
          {leftSidebar && !isMobile && (
            <div className="sticky top-20 max-h-[calc(100vh-5rem)] w-full max-w-xs flex-shrink-0 overflow-y-auto pb-[1rem]">
              {leftSidebar}
            </div>
          )}
          <div className="min-w-0 flex-1 space-y-6 pb-20 md:px-0">
            {children}
          </div>
          {rightSidebar && !isMobile && (
            <div className="w-full max-w-xs flex-shrink-0">{rightSidebar}</div>
          )}
        </div>
      </div>
    </div>
  )
}
